import { useTranslation } from "react-i18next";
import FormativeChart from "components/FormativeChart";
import { Skeleton, Typography } from "@mui/material";
import DataNotFound from "components/DataNotFound";
import { useAppSelector } from "app/hooks";
import {
  IFormativeData,
  ReportType,
  selectFormativeData,
  selectIsFormativeLoading,
  selectReportType,
} from "features/Reports";
import {
  transformClassesFormativeData,
  transformGradesFormativeData,
  transformStudentsFormativeReport,
  transformTeachersFormativeData,
} from "../Utils/FormativeHelpers";
import {
  IFormativeClassResponse,
  IFormativeGradeResponse,
  IFormativeGradesResponse,
  IFormativeTeacherResponse,
  IFormativeTeachersResponse,
} from "app/services/reports";

const customStyles = {
  root: {
    display: "flex",
    flexDirection: "column",
  },
  header: {
    flexGrow: 1,
    color: "rgba(60, 180, 229, 1)",
    fontWeight: "bold",
    "@media screen and (max-width: 1023px)": {
      fontSize: "12px",
    },
  },
  chartContainer: {
    direction: "ltr",
  },
};

export const getFormativeReport = (
  reportType: ReportType,
  teachersData?: IFormativeTeachersResponse,
  teacherData?: IFormativeTeacherResponse,
  classData?: IFormativeClassResponse,
  gradesData?: IFormativeGradesResponse,
  gradeData?: IFormativeGradeResponse
) => {
  let dataList: IFormativeData[] = [];
  let reportDate: string | undefined = undefined;

  try {
    const responseHandlers: Record<ReportType, () => void> = {
      [ReportType.Teachers_Report]: () => {
        ({ dataList, reportDate } = handleResponse(
          teachersData,
          transformTeachersFormativeData
        ));
      },
      [ReportType.Teacher_Report]: () => {
        ({ dataList, reportDate } = handleResponse(
          teacherData,
          transformClassesFormativeData
        ));
      },
      [ReportType.Grades_Report]: () => {
        ({ dataList, reportDate } = handleResponse(
          gradesData,
          transformGradesFormativeData
        ));
      },
      [ReportType.Grade_Report]: () => {
        ({ dataList, reportDate } = handleResponse(
          gradeData,
          transformClassesFormativeData
        ));
      },
      [ReportType.Class_Report]: () => {
        ({ dataList, reportDate } = handleResponse(
          classData,
          transformStudentsFormativeReport
        ));
      },
    };

    if (responseHandlers[reportType]) {
      responseHandlers[reportType]();
    } else {
      dataList = [];
    }
  } catch (error) {
    console.error(error);
  }

  return { dataList, reportDate };
};

interface IResponse {
  _statusCode?: number;
  _entity?: {
    _domainResponse?: any;
    _generationTime?: string;
  };
}

const handleResponse = <T extends IResponse>(
  data: T | undefined,
  transformFn: (response: any) => IFormativeData[]
): { dataList: IFormativeData[]; reportDate?: string } => {
  if (data?._statusCode === 200 && data?._entity?._domainResponse) {
    return {
      dataList: transformFn(data._entity._domainResponse),
      reportDate: data._entity._generationTime,
    };
  }
  return { dataList: [], reportDate: undefined };
};

function FormativeSection() {
  const { t } = useTranslation();
  const reportType = useAppSelector(selectReportType);
  const formativeData = useAppSelector(selectFormativeData);
  const isFormativeLoading = useAppSelector(selectIsFormativeLoading);

  const renderContent = () => {
    if (isFormativeLoading) {
      return <Skeleton variant="rectangular" height={250} />;
    }

    if (formativeData?.length && reportType) {
      return <FormativeChart data={formativeData} reportType={reportType} />;
    }

    return <DataNotFound text={t("No data")} />;
  };
  return (
    <Typography component="div" sx={customStyles.root}>
      <div style={{ display: "flex", marginBottom: "2rem" }}>
        <Typography component="span" sx={customStyles.header}>
          {t("Evaluation Test Results")}
        </Typography>
      </div>
      <Typography component="div" sx={customStyles.chartContainer}>
        {renderContent()}
      </Typography>
    </Typography>
  );
}

export default FormativeSection;
